<script lang="ts">
    import { onDestroy, onMount, tick } from 'svelte';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';

    import { api } from '../../../stores/api';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import { searchGuide, type GuideSearchHit } from './guideSearch';
    import {
        findChapter,
        guide,
        guideSearchQuery,
        clearGuideSearch,
        loadDocumentation,
        rememberScroll,
        selectChapter,
        selectPage,
        GUIDE_CHAPTER_URL,
    } from './guideStore';

    export let componentProps: { currentPage?: string } = {};

    let contentElement: HTMLDivElement | null = null;
    let initialized: boolean = false;
    let currentScrollTop: number = 0;
    let currentContentHeight: number = 0;
    let renderedSlug: string = '';
    // h2 headings of the current page (BookStack bkmrk anchors), shown as a submenu under the page
    let headings: { id: string; text: string }[] = [];
    // free-text search over all pages; a non-blank query switches the modal into searchMode. The
    // query lives in the store (guideSearchQuery) because the input is in the modal header.

    $: chapters = $guide.chapters;
    $: searchMode = $guideSearchQuery.trim() !== '';
    $: searchResults = searchMode ? searchGuide(chapters, $guideSearchQuery) : [];
    // in searchMode the left menu lists only pages present in the results (across ALL chapters),
    // grouped per chapter with its title - so the menu tracks the results instead of the active chapter
    $: searchMenuGroups = groupHitsByChapter(searchResults);
    $: selectedChapter = findChapter($guide, $guide.selectedChapterKey) ?? null;
    $: pages = selectedChapter?.pages ?? [];
    $: selectedSlug = $guide.selectedPageSlug;
    $: selectedPage = pages.find(page => page.slug === selectedSlug) ?? null;
    $: guideUrl = chapters[0]?.url ?? GUIDE_CHAPTER_URL;

    async function initialize(): Promise<void> {
        const state = get(guide);
        const chapters = state.chapters;
        if (initialized || chapters.length === 0) {
            return;
        }
        initialized = true;

        // The page context (guidePages.ts) may carry a bookmark, e.g.
        // 'podstawowa-konfiguracja#bkmrk-tworzenie-pojazdu'. Split it: the slug selects the page,
        // the hash (if any) scrolls to a heading once the content is rendered.
        const requested = componentProps?.currentPage ?? '';
        const colonIndex = requested.indexOf(':');
        const hashIndex = requested.indexOf('#');
        let requestedSlug = requested;
        let requestedHash = '';
        let requestedScrollTop = 0;
        if (colonIndex >= 0) {
            requestedSlug = requested.slice(0, colonIndex);
            requestedScrollTop = parseInt(requested.slice(colonIndex + 1));
        } else if (hashIndex >= 0) {
            requestedSlug = requested.slice(0, hashIndex);
            requestedHash = requested.slice(hashIndex + 1);
        }
        const requestedChapter = requestedSlug
            ? chapters.find(c => c.pages.some(page => page.slug === requestedSlug))
            : undefined;
        if (requestedChapter) {
            selectChapter(requestedChapter.key);
            selectPage(requestedSlug);
            // A mapped bookmark always wins over the remembered scroll position: jump straight to
            // that heading once the freshly selected page is in the DOM (same as openGuidePage).
            if (requestedScrollTop) {
                await tick();
                restoreScroll(requestedScrollTop);
            }
            if (requestedHash !== '') {
                await tick();
                scrollToAnchor('#' + requestedHash);
            }
            return;
        }

        // no explicit page - reopen exactly where the modal was closed
        const rememberedChapter = findChapter(state, state.selectedChapterKey);
        const remembered = state.selectedPageSlug;
        if (rememberedChapter && rememberedChapter.pages.some(page => page.slug === remembered)) {
            await tick();
            restoreScroll(state.scrollTop);
            return;
        }

        selectChapter(chapters[0].key);
    }

    /**
     * The container is still too short to scroll right after render (images are laid out later),
     * so the offset is re-applied until it sticks.
     */
    function restoreScroll(scrollTop: number, attemptsLeft: number = 40): void {
        const element = contentElement;
        if (!element || scrollTop <= 0) {
            return;
        }

        element.scrollTop = scrollTop;

        if ((Math.abs(element.scrollTop - scrollTop) > 3 || currentContentHeight != element?.scrollHeight) && attemptsLeft > 0) {
            currentContentHeight = element?.scrollHeight;
            setTimeout(() => restoreScroll(scrollTop, attemptsLeft - 1), 50);
        }
    }

    function openPage(slug: string): void {
        if (slug === selectedSlug) {
            return;
        }
        selectPage(slug);
    }

    /** Host of the guide (BookStack) derived from the configured chapter URL - links pointing there
     * are documentation pages we render in-app, so they must never open guide.automapa.pl. */
    function guideHost(): string {
        try {
            return new URL(GUIDE_CHAPTER_URL).host;
        } catch {
            return 'guide.automapa.pl';
        }
    }

    /** Scrolls to an element by id (BookStack bookmarks: id="bkmrk-...") within the content only,
     * so an in-page anchor never bubbles up to the app router / URL. */
    function scrollToAnchor(rawHash: string): void {
        const element = contentElement;
        if (!element) {
            return;
        }
        const raw = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
        if (raw === '') {
            return;
        }
        // BookStack ids are kept literal in the DOM (e.g. "bkmrk-wygl%C4%85d"), while a link's hash
        // may be either that literal form or its percent-decoded counterpart - try both.
        let decoded = raw;
        try {
            decoded = decodeURIComponent(raw);
        } catch {
            // keep the raw id if it is not valid percent-encoding
        }
        const target =
            element.querySelector(`[id="${CSS.escape(raw)}"]`) ??
            (decoded !== raw ? element.querySelector(`[id="${CSS.escape(decoded)}"]`) : null);
        if (target instanceof HTMLElement) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    /** Parses a guide page link into { slug, hash }, or null when it is not a BookStack page URL. */
    function parseGuidePageLink(href: string): { slug: string; hash: string } | null {
        let url: URL;
        try {
            url = new URL(href, window.location.href);
        } catch {
            return null;
        }
        if (url.host !== guideHost()) {
            return null;
        }
        const match = url.pathname.match(/\/page\/([^/]+)\/?$/);
        if (!match) {
            return null;
        }
        return { slug: decodeURIComponent(match[1]), hash: url.hash };
    }

    /** Switches the modal to another documentation page instead of leaving the app. Unknown slugs
     * are a no-op: the user stays in the modal rather than being sent to guide.automapa.pl. */
    async function openGuidePage(slug: string, hash: string): Promise<void> {
        const state = get(guide);
        const chapter = state.chapters.find(c => c.pages.some(page => page.slug === slug));
        if (!chapter) {
            return;
        }
        selectChapter(chapter.key);
        selectPage(slug);
        if (hash !== '') {
            await tick();
            scrollToAnchor(hash);
        }
    }

    interface SearchMenuGroup {
        chapterKey: string;
        chapterTitle: string;
        pages: { slug: string; title: string }[];
    }

    /** Groups flat search hits into per-chapter menu sections (preserving hit/chapter order), so the
     * searchMode menu lists only the matching pages, grouped under their chapter title. */
    function groupHitsByChapter(hits: GuideSearchHit[]): SearchMenuGroup[] {
        const groups: SearchMenuGroup[] = [];
        for (const hit of hits) {
            let group = groups.find(g => g.chapterKey === hit.chapterKey);
            if (!group) {
                group = { chapterKey: hit.chapterKey, chapterTitle: hit.chapterTitle, pages: [] };
                groups.push(group);
            }
            group.pages.push({ slug: hit.slug, title: hit.title });
        }
        return groups;
    }

    /** Opens a search result (possibly from another chapter), then leaves searchMode by clearing the
     * query - same page-switch pattern as openGuidePage/menu clicks. */
    function openHit(chapterKey: string, slug: string): void {
        selectChapter(chapterKey);
        selectPage(slug);
        clearGuideSearch();
    }

    /** Delegated handler for the injected {@html} content: keeps every documentation link inside
     * the app (in-page anchors scroll, cross-page links switch pages) instead of navigating away. */
    function handleContentClick(event: MouseEvent): void {
        const anchor = (event.target as HTMLElement | null)?.closest('a');
        if (!(anchor instanceof HTMLAnchorElement)) {
            return;
        }

        const href = anchor.getAttribute('href') ?? '';
        if (href === '') {
            return;
        }

        if (href.startsWith('#')) {
            event.preventDefault();
            scrollToAnchor(href);
            return;
        }

        const page = parseGuidePageLink(href);
        if (page) {
            event.preventDefault();
            void openGuidePage(page.slug, page.hash);
        }
    }

    /** Images in BookStack are wrapped in an anchor to the full-size file (target=_blank). Unwrap
     * them so an image is plain inline content and cannot be clicked open in a new tab. */
    function unwrapImageLinks(): void {
        const element = contentElement;
        if (!element) {
            return;
        }
        element.querySelectorAll('a img').forEach(img => {
            const anchor = img.closest('a');
            if (anchor) {
                anchor.replaceWith(...anchor.childNodes);
            }
        });
    }

    /** BookStackParser leaves guide images without a src (guide.automapa.pl resolves to a private
     * IP the browser would refuse/prompt on); it stashes the asset path in data-guide-asset instead.
     * Point each one at the same-origin backend proxy, which fetches the image server-side. Both the
     * API host and token are known here, so the cached HTML stays host- and token-free. */
    function resolveGuideImages(): void {
        const element = contentElement;
        if (!element) {
            return;
        }
        const base = $api.getApiUrl();
        element.querySelectorAll<HTMLImageElement>('img[data-guide-asset]').forEach(img => {
            const path = img.dataset.guideAsset ?? '';
            if (path === '') {
                return;
            }
            img.src = `${base}/guide/proxy_asset?filter[path]=${encodeURIComponent(path)}`;
        });
    }

    /** Collects the page's h2 headings (each carries an id="bkmrk-...") so they can be listed under
     * the page in the menu and scrolled to. Runs after the {@html} body is in the DOM. */
    function collectHeadings(): void {
        const element = contentElement;
        if (!element) {
            headings = [];
            return;
        }
        headings = Array.from(element.querySelectorAll('h2[id]'))
            .map(h => ({ id: h.id, text: (h.textContent ?? '').trim() }))
            .filter(h => h.id !== '' && h.text !== '');
    }

    function handleScroll(): void {
        currentScrollTop = contentElement?.scrollTop ?? currentScrollTop;
    }

    // a page switch - from this menu or from the chapter select in the modal header - starts at the top
    $: if (selectedSlug !== renderedSlug) {
        const firstRender = renderedSlug === '';
        renderedSlug = selectedSlug;
        currentScrollTop = 0;
        // drop the previous page's segments until the new body is processed below
        headings = [];
        if (!firstRender && contentElement) {
            contentElement.scrollTop = 0;
        }
    }

    // the {@html} body lands in the DOM after this cycle, so post-process (unwrap image links,
    // collect h2 headings) on the next tick
    $: if (selectedPage && contentElement) {
        void tick().then(() => {
            resolveGuideImages();
            unwrapImageLinks();
            collectHeadings();
        });
    }

    onMount(async () => {
        // initializing outside a reactive block on purpose: an assignment made from one would not
        // re-run the already evaluated `selectedPage` derivation in the same update cycle
        await loadDocumentation($api);
        await initialize();
    });

    onDestroy(() => {
        // the element binding is already gone here, hence the scroll position tracked on the fly
        rememberScroll(currentScrollTop);
    });
</script>

<div class="documentation">
    {#if $guide.loading && chapters.length === 0}
        <div class="centered"><Spinner /></div>
    {:else if $guide.error && chapters.length === 0}
        <div class="centered error">
            <div>{$_('documentation_load_error')}</div>
            <a href={guideUrl} target="_blank" rel="noopener noreferrer">
                {guideUrl}
            </a>
        </div>
    {:else}
        <div class="layout">
            <nav class="menu">
                {#if searchMode}
                    <!-- searchMode: only pages present in the results, grouped per chapter -->
                    {#each searchMenuGroups as group (group.chapterKey)}
                        <div class="menu-chapter">{group.chapterTitle}</div>
                        {#each group.pages as page (page.slug)}
                            <button
                                type="button"
                                class="menu-item"
                                on:click={() => openHit(group.chapterKey, page.slug)}
                            >
                                {page.title}
                            </button>
                        {/each}
                    {/each}
                {:else}
                    {#each pages as page (page.slug)}
                        <button
                            type="button"
                            class="menu-item"
                            class:active={page.slug === selectedSlug}
                            on:click={() => openPage(page.slug)}
                        >
                            {page.title}
                        </button>
                        {#if page.slug === selectedSlug && headings.length > 0}
                            <div class="segments">
                                {#each headings as heading (heading.id)}
                                    <button
                                        type="button"
                                        class="segment-item"
                                        on:click={() => scrollToAnchor('#' + heading.id)}
                                    >
                                        {heading.text}
                                    </button>
                                {/each}
                            </div>
                        {/if}
                    {/each}
                {/if}
            </nav>

            <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
            <div
                class="content"
                bind:this={contentElement}
                on:scroll={handleScroll}
                on:click={handleContentClick}
            >
                {#if searchMode}
                    {#if searchResults.length === 0}
                        <div class="search-empty">{$_('documentation_search_no_results')}</div>
                    {:else}
                        <div class="search-results">
                            {#each searchResults as hit (hit.chapterKey + '/' + hit.slug)}
                                <button
                                    type="button"
                                    class="search-result"
                                    on:click={() => openHit(hit.chapterKey, hit.slug)}
                                >
                                    <span class="search-result-title">{hit.title}</span>
                                    <span class="search-result-snippet">
                                        <!-- highlight from segments, never {@html} on raw content -->
                                        {#each hit.snippet as seg}{#if seg.matched}<mark
                                                >{seg.text}</mark
                                            >{:else}{seg.text}{/if}{/each}
                                    </span>
                                </button>
                            {/each}
                        </div>
                    {/if}
                {:else if selectedPage}
                    <!-- the REST API returns the page body only, so the title is rendered here -->
                    <h1>{selectedPage.title}</h1>
                    <!-- sanitized server-side: BookStackParser strips scripts, styles and on* handlers -->
                    {@html selectedPage.html}
                {/if}
            </div>
        </div>
    {/if}
</div>

<style>
    .documentation {
        /* guide primary/link colour (BookStack --color-link) so the content matches guide.automapa.pl */
        --guide-primary: #206ea7;
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        font-size: 14px;
        /* guide body font stack (--font-body) */
        font-family:
            -apple-system, BlinkMacSystemFont, 'Segoe UI', Oxygen, Ubuntu, Roboto, Cantarell,
            'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    }

    .centered {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        row-gap: 8px;
    }

    .error {
        color: #c0392b;
    }

    .layout {
        display: flex;
        flex-direction: row;
        column-gap: 16px;
        flex: 1 1 auto;
        min-height: 0;
        /*padding: 15px;*/
    }

    .menu {
        display: flex;
        flex-direction: column;
        row-gap: 2px;
        flex: 0 0 230px;
        width: 230px;
        overflow-y: auto;
        border-right: 1px solid #0001;
        padding: 15px;
        background-color: #f2f2f2;
    }

    /* search results list rendered in the content area while searchMode is active */
    .search-results {
        display: flex;
        flex-direction: column;
        row-gap: 10px;
    }

    .search-result {
        display: flex;
        flex-direction: column;
        row-gap: 4px;
        text-align: left;
        background: none;
        border: 1px solid #0002;
        border-radius: 6px;
        padding: 12px 14px;
        cursor: pointer;
    }

    .search-result:hover {
        background-color: #1c5dc711;
        border-color: #1c5dc7;
    }

    .search-result-title {
        font-weight: 600;
        color: #62a025;
        font-size: 1.05em;
    }

    .search-result-snippet {
        color: #444;
        line-height: 1.5;
    }

    .search-result-snippet :global(mark),
    .search-result-snippet mark {
        background-color: #ffe08a;
        color: inherit;
        padding: 0 1px;
        border-radius: 2px;
    }

    .search-empty {
        color: #888;
        font-style: italic;
    }

    /* chapter title header shown above its matching pages while searchMode is active */
    .menu-chapter {
        margin: 8px 0 2px 0;
        padding: 0 10px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: #888;
    }

    .menu-item {
        text-align: left;
        background: none;
        border: none;
        border-radius: 6px;
        padding: 8px 10px;
        font-size: 13px;
        line-height: 1.35;
        /*color: #000c;*/
        cursor: pointer;
        font-weight: 600;
        color: #333;
    }

    .menu-item:hover {
        background-color: #1c5dc711;
    }

    .menu-item.active {
        /*background-color: var(--automapa-blue, #1c5dc7);*/
        /*background-color: var(--main-selected-background);*/
        background-color: #1c5dc7;
        color: white;
        /*color: #fff;*/
        /*font-weight: 600;*/
    }

    /* segments (h2 headings) of the active page, indented under it */
    .segments {
        display: flex;
        flex-direction: column;
        row-gap: 1px;
        margin: 2px 0 4px 0;
        border-left: 2px solid #62a02533;
        padding-left: 6px;
    }

    .segment-item {
        text-align: left;
        background: none;
        border: none;
        border-radius: 6px;
        padding: 5px 10px;
        font-size: 12px;
        line-height: 1.3;
        cursor: pointer;
        font-weight: 500;
        color: #62a025;
    }

    .segment-item:hover {
        background-color: #1c5dc711;
        color: #1c5dc7;
    }

    /* content typography mirrors guide.automapa.pl (BookStack reader): text #444, headings #222, blue accents */
    .content {
        flex: 1 1 auto;
        overflow-y: auto;
        line-height: 1.6;
        color: #444;
        padding: 35px 45px;
    }

    .content :global(img) {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
    }

    /* embedded players (e.g. YouTube) kept by BookStackParser: never overflow the modal width */
    .content :global(iframe) {
        max-width: 100%;
        border-radius: 4px;
        border: 0;
    }

    /*
     * BookStack aligns images two ways, both of which reach us verbatim (the parser keeps class):
     *  1) on the wrapping block (<p class="align-center">) — an inline <img> follows text-align, or
     *  2) on the <img> itself (class="align-left|align-center|align-right") — needs float / auto margins.
     * Only case 1 for centre was handled, so left/right-aligned images and img-level classes all
     * fell back to the default left. Mirror the BookStack reader CSS for every case.
     */
    .content :global(.align-left) {
        text-align: left;
    }

    .content :global(.align-center) {
        text-align: center;
    }

    .content :global(.align-right) {
        text-align: right;
    }

    .content :global(img.align-center) {
        display: block;
        margin-left: auto;
        margin-right: auto;
    }

    .content :global(img.align-left) {
        float: left;
        margin: 0.5em 1em 0.5em 0;
    }

    .content :global(img.align-right) {
        float: right;
        margin: 0.5em 0 0.5em 1em;
    }

    .content :global(h1),
    .content :global(h2),
    .content :global(h3),
    .content :global(h4),
    .content :global(h5),
    .content :global(h6) {
        font-weight: 400;
        color: #222;
        line-height: 1.3;
    }

    .content :global(h1) {
        font-size: 1.9em;
        margin: 0 0 0.5em;
        color: #62a025;
    }

    .content :global(h2) {
        font-size: 1.55em;
        margin: 1.2em 0 0.4em;
        color: #62a025;
    }

    .content :global(h3) {
        font-size: 1.3em;
        margin: 1.1em 0 0.4em;
        color: #62a025;
    }

    .content :global(h4) {
        font-size: 1.1em;
        margin: 1em 0 0.4em;
        color: #62a025;
    }

    .content :global(h5),
    .content :global(h6) {
        font-size: 1em;
        margin: 1em 0 0.3em;
        color: #62a025;
    }

    .content :global(p) {
        margin: 0 0 1em;
    }

    .content :global(a) {
        color: var(--guide-primary);
        text-decoration: underline;
        transition: filter ease-in-out 80ms;
    }

    /*.content :global(a:hover) {*/
    /*    filter: brightness(1.15);*/
    /*}*/

    .content :global(ul) {
        padding-left: 1.5em;
        list-style: disc;
    }

    .content :global(ol) {
        padding-left: 1.5em;
        list-style: decimal;
    }

    .content :global(li) {
        margin: 0.25em 0;
    }

    .content :global(table) {
        border-collapse: collapse;
        max-width: 100%;
    }

    .content :global(th),
    .content :global(td) {
        border: 1px solid #ddd;
        padding: 6px 8px;
        line-height: 1.2;
        vertical-align: top;
    }

    .content :global(th) {
        font-weight: bold;
        text-align: start;
    }

    .content :global(hr) {
        border: 0;
        height: 1px;
        background: #eaeaea;
        margin: 24px 0;
    }

    .content :global(blockquote) {
        margin: 1em 0;
        padding: 12px 16px 12px 32px;
        border-left: 4px solid var(--guide-primary);
        background-color: #f8f8f8;
        overflow: auto;
    }

    .content :global(code) {
        font-family: 'Lucida Console', 'DejaVu Sans Mono', 'Ubuntu Mono', Monaco, monospace;
        font-size: 0.84em;
        border: 1px solid #ddd;
        border-radius: 3px;
        background-color: #f8f8f8;
        padding: 1px 3px;
        white-space: pre-wrap;
    }

    .content :global(pre) {
        font-family: 'Lucida Console', 'DejaVu Sans Mono', 'Ubuntu Mono', Monaco, monospace;
        font-size: 12px;
        border: 1px solid #ddd;
        background-color: #fff;
        border-radius: 4px;
        padding: 12px;
        overflow: auto;
    }

    .content :global(pre code) {
        border: 0;
        background: none;
        padding: 0;
        font-size: inherit;
    }

    /* BookStack callouts (info/success/warning/danger) - ported from guide styles.css */
    .content :global(.callout) {
        display: block;
        position: relative;
        overflow: auto;
        margin: 1em 0;
        padding: 12px 12px 12px 32px;
        border-left: 4px solid #bbb;
        border-radius: 0 4px 4px 0;
        background-color: #eee;
    }

    .content :global(.callout)::before {
        content: '';
        position: absolute;
        left: 8px;
        top: 12px;
        width: 1.2em;
        height: 1.2em;
        background-repeat: no-repeat;
        opacity: 0.8;
        background-image: url('data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9IiMwMTUzODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+ICAgIDxwYXRoIGQ9Ik0wIDBoMjR2MjRIMHoiIGZpbGw9Im5vbmUiLz4gICAgPHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTEgMTVoLTJ2LTZoMnY2em0wLThoLTJWN2gydjJ6Ii8+PC9zdmc+');
    }

    .content :global(.callout.info) {
        border-color: #0288d1;
        background-color: #d3efff;
        color: #01466c;
    }

    .content :global(.callout.success) {
        border-color: #0f7d15;
        background-color: #eafdeb;
        color: #063409;
    }

    .content :global(.callout.warning) {
        border-color: #cf4d03;
        background-color: #fee3d3;
        color: #6a2802;
    }

    .content :global(.callout.danger) {
        border-color: #ab0f0e;
        background-color: #fcdbdb;
        color: #4d0706;
    }

    .content :global(.callout.success)::before {
        background-image: url('data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9IiMzNzZjMzkiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+ICAgIDxwYXRoIGQ9Ik0wIDBoMjR2MjRIMHoiIGZpbGw9Im5vbmUiLz4gICAgPHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bS0yIDE1bC01LTUgMS40MS0xLjQxTDEwIDE0LjE3bDcuNTktNy41OUwxOSA4bC05IDl6Ii8+PC9zdmc+');
    }

    .content :global(.callout.warning)::before {
        background-image: url('data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9IiNiNjUzMWMiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+ICAgIDxwYXRoIGQ9Ik0wIDBoMjR2MjRIMHoiIGZpbGw9Im5vbmUiLz4gICAgPHBhdGggZD0iTTEgMjFoMjJMMTIgMiAxIDIxem0xMi0zaC0ydi0yaDJ2MnptMC00aC0ydi00aDJ2NHoiLz48L3N2Zz4=');
    }

    .content :global(.callout.danger)::before {
        background-image: url('data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9IiNiOTE4MTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+ICAgIDxwYXRoIGQ9Ik0xNS43MyAzSDguMjdMMyA4LjI3djcuNDZMOC4yNyAyMWg3LjQ2TDIxIDE1LjczVjguMjdMMTUuNzMgM3pNMTIgMTcuM2MtLjcyIDAtMS4zLS41OC0xLjMtMS4zIDAtLjcyLjU4LTEuMyAxLjMtMS4zLjcyIDAgMS4zLjU4IDEuMyAxLjMgMCAuNzItLjU4IDEuMy0xLjMgMS4zem0xLTQuM2gtMlY3aDJ2NnoiLz4gICAgPHBhdGggZD0iTTAgMGgyNHYyNEgweiIgZmlsbD0ibm9uZSIvPjwvc3ZnPg==');
    }

    .content :global(.callout a) {
        color: inherit;
        text-decoration: underline;
    }
</style>
